import { useReducer, useState } from 'react';
import { useMutation } from '@apollo/client';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ThumbsUp, ThumbsDown, ArrowRight } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger, Input } from '@artstorefronts/ui';
import { UPDATE_CHAT_PART_EVALUATION_MUTATION } from '~/graphql/mutations';
import { useAnalytics } from '~/context/analytics';

function reducer(state: any, action: any): any {
  switch (action.type) {
    case 'SET_RATING':
      return { ...state, rating: action.rating };
    case 'START_SUBMIT':
      return { ...state, submitting: true };
    case 'END_SUBMIT':
      return { ...state, submitting: false };
    default:
      return state;
  }
}

function ChatPartRating({
  chatPartId,
  initialGood,
  initialNotes,
}: {
  chatPartId: string;
  initialGood?: boolean | null;
  initialNotes?: string | null;
}) {
  const { t } = useTranslation('chat');
  const [updateChatPartEvaluation] = useMutation(UPDATE_CHAT_PART_EVALUATION_MUTATION);
  const [open, setOpen] = useState(false);

  const [state, dispatch] = useReducer(reducer, {
    rating: initialGood ?? null, // Fix: use nullish coalescing to handle false correctly
    submitting: false,
  });

  const { amplitude, intercom } = useAnalytics();

  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<any>({
    defaultValues: {
      notes: initialNotes || '',
    },
  });

  const handleRating = async (e: React.MouseEvent, good: boolean) => {
    e.stopPropagation(); // Prevent trigger from opening popover
    dispatch({ type: 'START_SUBMIT' });
    try {
      await updateChatPartEvaluation({
        variables: {
          input: {
            id: chatPartId,
            good,
            notes: null, // Clear notes when just rating
          },
        },
        optimisticResponse: {
          updateChatPartEvaluation: {
            __typename: 'ChatPart',
            id: chatPartId,
            good,
            notes: null,
            updatedAt: new Date().toISOString(),
          },
        },
      });
      dispatch({ type: 'SET_RATING', rating: good });
      amplitude.track('chat_rating_clicked', {
        good,
      });
      intercom.trackEvent('chat_rating_clicked', {
        good,
      });
      setOpen(true); // Open popover for feedback
    } catch (error) {
      console.error('Error saving chat part evaluation:', error);
    } finally {
      dispatch({ type: 'END_SUBMIT' });
    }
  };

  const onSubmit = async (data: any) => {
    if (!data.notes.trim()) return;

    try {
      await updateChatPartEvaluation({
        variables: {
          input: {
            id: chatPartId,
            good: state.rating,
            notes: data.notes.trim(),
          },
        },
      });
      setOpen(false);
    } catch (error) {
      console.error('Error saving feedback:', error);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className="flex gap-1">
          {(state.rating === null || state.rating === true) && (
            <button
              onClick={(e) => handleRating(e, true)}
              disabled={state.submitting || state.rating === true}
              className={`p-1.5 rounded-md transition-colors text-gray-500 hover:text-gray-700 hover:bg-gray-100 ${
                state.rating === true ? 'bg-gray-100 text-gray-700' : ''
              }`}
              title={t('rating.mark-as-good')}
            >
              <ThumbsUp className={`w-4 h-4 ${state.rating === true ? 'fill-current' : ''}`} />
            </button>
          )}

          {(state.rating === null || state.rating === false) && (
            <button
              onClick={(e) => handleRating(e, false)}
              disabled={state.submitting || state.rating === false}
              className={`p-1.5 rounded-md transition-colors text-gray-500 hover:text-gray-700 hover:bg-gray-100 ${
                state.rating === false ? 'bg-gray-100 text-gray-700' : ''
              }`}
              title={t('rating.mark-as-bad')}
            >
              <ThumbsDown className={`w-4 h-4 ${state.rating === false ? 'fill-current' : ''}`} />
            </button>
          )}
        </div>
      </PopoverTrigger>

      <PopoverContent side="right" className="w-80 p-3 rounded-xl">
        <form onSubmit={handleSubmit(onSubmit)} className="flex items-center gap-4">
          <Input
            {...register('notes')}
            placeholder={t('rating.add-feedback')}
            className="flex-1 text-xs rounded-full h-8"
            onKeyDown={(e: any) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit(onSubmit)();
              }
              if (e.key === 'Escape') {
                setOpen(false);
              }
            }}
          />
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-8 h-8 bg-black text-white rounded-full flex items-center justify-center hover:bg-gray-800 disabled:opacity-50 flex-shrink-0"
            title={t('rating.send-feedback')}
          >
            <ArrowRight className="w-4 h-4 fill-current" />
          </button>
        </form>
      </PopoverContent>
    </Popover>
  );
}

export default ChatPartRating;
